import { getRuntimeConfig } from '../../app/runtimeConfig';

export interface ApiRequestOptions extends RequestInit {
  readonly path: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type RefreshAuthHeaders = () => Promise<Record<string, string> | null>;

let refreshAuthHeaders: RefreshAuthHeaders | null = null;

export function configureApiAuthHandlers(handlers: { refreshAuthHeaders: RefreshAuthHeaders | null }) {
  refreshAuthHeaders = handlers.refreshAuthHeaders;
}

export async function apiRequest<T>(options: ApiRequestOptions): Promise<T> {
  const config = getRuntimeConfig();
  const baseHeaders = new Headers(options.headers);
  if (!baseHeaders.has('Content-Type') && options.body) {
    baseHeaders.set('Content-Type', 'application/json');
  }

  const response = await runRequest(`${config.apiBaseUrl}${options.path}`, options, baseHeaders);
  if (response.ok) {
    return parseResponse<T>(response);
  }

  const shouldRetry =
    (response.status === 401 || response.status === 403)
    && Boolean(baseHeaders.get('Authorization'))
    && Boolean(refreshAuthHeaders);

  if (shouldRetry) {
    const refreshed = await refreshAuthHeaders?.();
    if (refreshed?.Authorization) {
      const retryHeaders = new Headers(baseHeaders);
      retryHeaders.set('Authorization', refreshed.Authorization);
      const retryResponse = await runRequest(`${config.apiBaseUrl}${options.path}`, options, retryHeaders);
      if (retryResponse.ok) {
        return parseResponse<T>(retryResponse);
      }
      throw await toApiError(retryResponse);
    }
  }

  throw await toApiError(response);
}

async function runRequest(url: string, options: ApiRequestOptions, headers: Headers): Promise<Response> {
  return fetch(url, {
    ...options,
    headers,
  });
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

async function toApiError(response: Response): Promise<ApiError> {
  const errorText = await response.text();
  try {
    const errorPayload = JSON.parse(errorText) as { message?: string; code?: string };
    return new ApiError(
      errorPayload.message || errorPayload.code || `API request failed: ${response.status}`,
      response.status,
      errorPayload.code
    );
  } catch {
    return new ApiError(errorText || `API request failed: ${response.status}`, response.status);
  }
}
